// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import type { QueryClient } from "@tanstack/react-query";
import type { PluginListenerHandle } from "@capacitor/core";
import { Style } from "@capacitor/status-bar";
import i18n from "@/i18n";
import { router as appRouter } from "@/router";
import { queryClient as appQueryClient } from "@/lib/query-client";
import { vaultCrypto } from "@/services/vault-crypto";
import { mountLocalReminderScheduler } from "./localReminders";
import { native } from "./index";

type ListenerHandle = PluginListenerHandle | Promise<PluginListenerHandle>;

interface NativeLifecycleOptions {
  router?: typeof appRouter;
  queryClient?: QueryClient;
  lockVault?: () => void;
  confirmExit?: (message: string) => boolean;
  exitApp?: () => Promise<void>;
  documentElement?: HTMLElement;
}

let cleanupCurrent: (() => void) | null = null;

function isDarkMode(documentElement: HTMLElement): boolean {
  return documentElement.classList.contains("dark");
}

async function applyStatusBarTheme(documentElement: HTMLElement): Promise<void> {
  await native.statusBar.setStyle({
    style: isDarkMode(documentElement) ? Style.Dark : Style.Light,
  });
}

function routeDeepLink(url: string, router: typeof appRouter): void {
  const prefix = "https://pssmngr.com/app/";
  const toDashboardPath = (path: string) => {
    const clean = path.replace(/^\/+/, "");
    if (clean === "vault/health") return "/dashboard/watchtower";
    return clean.startsWith("dashboard") ? `/${clean}` : `/dashboard/${clean}`;
  };

  if (url.startsWith(prefix)) {
    router.history.push(toDashboardPath(url.slice(prefix.length)));
    return;
  }

  if (url.startsWith("/app/")) {
    router.history.push(toDashboardPath(url.slice("/app/".length)));
  }
}

function isRootPath(pathname: string): boolean {
  return pathname === "/" || pathname === "/dashboard";
}

function invalidateShortTtlQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) =>
      query.meta?.ttl === "short" ||
      query.meta?.shortTtl === true ||
      query.queryKey.includes("session"),
  });
}

export function mountNativeLifecycle(
  options: NativeLifecycleOptions = {},
): () => void {
  cleanupCurrent?.();

  const router = options.router ?? appRouter;
  const queryClient = options.queryClient ?? appQueryClient;
  const lockVault = options.lockVault ?? (() => vaultCrypto.lock());
  const confirmExit = options.confirmExit ?? ((message) => window.confirm(message));
  const exitApp = options.exitApp ?? (() => native.app.exitApp());
  const documentElement = options.documentElement ?? document.documentElement;
  const handles: ListenerHandle[] = [];
  const canScheduleLocalReminders =
    typeof (native.localNotifications as { addListener?: unknown }).addListener ===
    "function";
  const cleanupLocalReminders = canScheduleLocalReminders
    ? mountLocalReminderScheduler(router)
    : () => {};

  void applyStatusBarTheme(documentElement);

  const observer = new MutationObserver(() => {
    void applyStatusBarTheme(documentElement);
  });
  observer.observe(documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });

  handles.push(
    native.app.addListener("appUrlOpen", ({ url }) => {
      routeDeepLink(url, router);
    }),
  );

  handles.push(
    native.app.addListener("backButton", () => {
      if (!isRootPath(router.state.location.pathname)) {
        router.history.back();
        return;
      }

      const shouldExit = confirmExit(
        i18n.t("mobile:exit_confirm.description", "Exit Password Manager?"),
      );

      if (shouldExit) {
        void exitApp();
      }
    }),
  );

  handles.push(
    native.app.addListener("appStateChange", ({ isActive }) => {
      lockVault();

      if (isActive) {
        invalidateShortTtlQueries(queryClient);
      }
    }),
  );

  cleanupCurrent = () => {
    observer.disconnect();
    cleanupLocalReminders();
    for (const handle of handles) {
      void Promise.resolve(handle).then((resolved) => resolved.remove());
    }
    cleanupCurrent = null;
  };

  return cleanupCurrent;
}

export const __testing = {
  invalidateShortTtlQueries,
  isRootPath,
  routeDeepLink,
};
